import secureRequest from "@toothfairy/shared-api/secureAPI";
import envConfig from "../../../envConfig";
import client from "@toothfairy/shared-api/src/Common/client";

/**
 * Live Sessions API — mixed-mode (text + voice) conversations.
 * Thin client over the /live service: adopt an existing chat as a live
 * session, start/stop realtime voice segments on it, read the unified
 * transcript. Auth is the workspace token (x-api-key), same as Media.
 */

const LIVE_BASE = () => `${envConfig.REACT_APP_LIVE_API_ENDPOINT}`;

/**
 * Create (or adopt) a live session for a chat. Pass the existing chatId so
 * the session binds to the current conversation; if that chat already has
 * an active session (e.g. another tab), it is returned with adopted=true.
 */
const createLiveSession = async ({ token, workspaceid, agentId, chatId, name }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "POST",
    `${LIVE_BASE()}/live`,
    null,
    {
      workspace_id: workspaceid,
      agent_id: agentId,
      chat_id: chatId,
      ...(name ? { name } : {}),
    },
    false,
    headers,
  );
};

/** Start a realtime voice segment. Returns {token, url, room_name}. */
const startLiveVoice = async ({ token, sessionId, agentId, userId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "POST",
    `${LIVE_BASE()}/live/${sessionId}/voice/start`,
    null,
    {
      ...(agentId ? { agent_id: agentId } : {}),
      ...(userId ? { user_id: userId } : {}),
    },
    false,
    headers,
  );
};

/** Stop the active voice segment (releases the backend mode lock). */
const stopLiveVoice = async ({ token, sessionId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "POST",
    `${LIVE_BASE()}/live/${sessionId}/voice/stop`,
    null,
    {},
    false,
    headers,
  );
};

/** Session status: mode (idle|text|voice), status (active|ended). */
const getLiveSession = async ({ token, sessionId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "GET",
    `${LIVE_BASE()}/live/${sessionId}`,
    null,
    null,
    false,
    headers,
  );
};

/** Unified transcript (text + voice turns) with per-message agentID. */
const getLiveTranscript = async ({ token, sessionId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "GET",
    `${LIVE_BASE()}/live/${sessionId}/transcript`,
    null,
    null,
    false,
    headers,
  );
};

/** End the session (the chat persists). */
const endLiveSession = async ({ token, sessionId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "POST",
    `${LIVE_BASE()}/live/${sessionId}/end`,
    null,
    {},
    false,
    headers,
  );
};

/**
 * Attach files/images to the chat as context (not a turn — works during
 * both voice and text modes). Keys are the standard upload S3 keys.
 */
const createLiveAttachments = async ({ token, sessionId, files, images, userId }) => {
  let headers;
  if (token) headers = { "x-api-key": token };
  return secureRequest(
    client,
    "POST",
    `${LIVE_BASE()}/live/${sessionId}/attachments`,
    null,
    {
      ...(files?.length ? { files } : {}),
      ...(images?.length ? { images } : {}),
      ...(userId ? { user_id: userId } : {}),
    },
    false,
    headers,
  );
};

export default {
  createLiveSession,
  startLiveVoice,
  stopLiveVoice,
  getLiveSession,
  getLiveTranscript,
  endLiveSession,
  createLiveAttachments,
};
